/**
 * HOMING MISSILES — the player's automatic firepower.
 * Spec: bible 06-SHIPS-AND-WEAPONS §3.
 *
 * Picked up as a timed loadout rather than as ammunition. For the duration,
 * the aircraft launches seeking missiles on its own while the player keeps
 * flying and dodging — so it reads as the airframe fighting alongside you
 * rather than as another button to manage.
 *
 * DESIGN RESTRAINT. Missiles must not replace the main gun. They fire on a
 * slow cadence, carry modest damage, and cannot reach terrain-locked targets.
 * What they add is PRESSURE while the player is busy surviving — which is
 * exactly the fantasy of "badass firepower" without deleting the core skill.
 *
 * Per the armour table (game/combat.ts) HOMING is 1.20 vs LIGHT and 0.85 vs
 * ARMORED — it shreds swarms and struggles against hulls. So it is strong in
 * the moments a swarm has the player pinned, and honest everywhere else.
 */

import * as THREE from 'three'

const MAX = 24

export const HOMING = {
  /** Seconds a pickup lasts. */
  duration: 18,
  /** Volleys per second while active. */
  cadence: 1.6,
  /** Missiles per volley. */
  perVolley: 2,
  speed: 34,
  /** Degrees per second the missile can turn — high enough to seek, low
   *  enough that a fast target can still break lock. */
  turnRate: 260,
  damage: 26,
  lifetime: 2.4,
  /** Acquisition range in world units. */
  acquire: 15,
} as const

export class HomingSystem {
  readonly group = new THREE.Group()

  private readonly active = new Uint8Array(MAX)
  private readonly x = new Float32Array(MAX)
  private readonly y = new Float32Array(MAX)
  private readonly vx = new Float32Array(MAX)
  private readonly vy = new Float32Array(MAX)
  private readonly life = new Float32Array(MAX)
  private readonly target = new Int16Array(MAX)

  private readonly mesh: THREE.InstancedMesh;
  private readonly trail: THREE.InstancedMesh;
  private readonly dummy = new THREE.Object3D()

  /** Seconds of loadout remaining. */
  remaining = 0
  private volleyTimer = 0
  private launchSide = 1

  constructor() {
    this.group.name = 'HomingMissiles'
    const body = new THREE.CapsuleGeometry(0.075, 0.34, 3, 6)
    const mat = new THREE.MeshBasicMaterial({
      color: '#ffe0a8',
      toneMapped: false,
      depthWrite: false,
    })
    this.mesh = new THREE.InstancedMesh(body, mat, MAX)
    this.mesh.frustumCulled = false
    this.mesh.count = 0
    this.mesh.renderOrder = 21
    this.group.add(this.mesh)

    // Exhaust plume — additive, so a salvo reads as a streak of light.
    const plume = new THREE.CircleGeometry(0.2, 8)
    const plumeMat = new THREE.MeshBasicMaterial({
      color: '#ff9a3c',
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
    this.trail = new THREE.InstancedMesh(plume, plumeMat, MAX)
    this.trail.frustumCulled = false
    this.trail.count = 0
    this.trail.renderOrder = 20
    this.group.add(this.trail)
  }

  get active_(): boolean {
    return this.remaining > 0
  }

  /** Grant or refresh the loadout. Refreshing resets the clock, not stacks it. */
  grant(): void {
    this.remaining = HOMING.duration
  }

  /**
   * Advance. Launches automatically while the loadout is live, then steers
   * every missile toward its acquired target.
   */
  update(
    dt: number,
    px: number,
    py: number,
    enemyCount: number,
    eActive: Uint8Array,
    ex: Float32Array,
    ey: Float32Array,
    onHit: (index: number, x: number, y: number) => void,
  ): void {
    if (this.remaining > 0) {
      this.remaining -= dt
      this.volleyTimer -= dt
      if (this.volleyTimer <= 0) {
        this.volleyTimer = 1 / HOMING.cadence
        for (let i = 0; i < HOMING.perVolley; i += 1) {
          // Alternate wings so the launch reads as a pair of hardpoints.
          this.launch(px + this.launchSide * 0.7, py + 0.6, this.launchSide)
          this.launchSide *= -1
        }
      }
    }

    for (let i = 0; i < MAX; i += 1) {
      if (!this.active[i]) continue
      this.life[i] -= dt
      if (this.life[i] <= 0) {
        this.active[i] = 0
        continue
      }

      // Re-acquire if the current target died or was never set.
      let t = this.target[i]
      if (t < 0 || !eActive[t]) t = this.acquire(this.x[i], this.y[i], enemyCount, eActive, ex, ey)
      this.target[i] = t

      if (t >= 0) {
        // Steer toward the target at a bounded turn rate — a missile that
        // turns instantly is a hitscan weapon wearing a costume.
        const dx = ex[t] - this.x[i]
        const dy = ey[t] - this.y[i]
        const desired = Math.atan2(dy, dx)
        const current = Math.atan2(this.vy[i], this.vx[i])
        let diff = desired - current
        while (diff > Math.PI) diff -= Math.PI * 2
        while (diff < -Math.PI) diff += Math.PI * 2
        const maxTurn = (HOMING.turnRate * Math.PI) / 180 * dt
        const turn = Math.max(-maxTurn, Math.min(maxTurn, diff))
        const a = current + turn
        this.vx[i] = Math.cos(a) * HOMING.speed
        this.vy[i] = Math.sin(a) * HOMING.speed

        // Impact.
        if (dx * dx + dy * dy < 1.2 * 1.2) {
          onHit(t, ex[t], ey[t])
          this.active[i] = 0
          continue
        }
      }

      this.x[i] += this.vx[i] * dt
      this.y[i] += this.vy[i] * dt
      if (this.y[i] > 28 || this.y[i] < -28 || Math.abs(this.x[i]) > 22) this.active[i] = 0
    }

    this.sync()
  }

  private launch(x: number, y: number, side: number): void {
    let i = -1
    for (let k = 0; k < MAX; k += 1) if (!this.active[k]) { i = k; break }
    if (i < 0) return
    this.active[i] = 1
    this.x[i] = x
    this.y[i] = y
    // Launch outward and up, so the salvo arcs before it turns in — that
    // curve is most of what makes homing missiles look good.
    this.vx[i] = side * 16
    this.vy[i] = 26
    this.life[i] = HOMING.lifetime
    this.target[i] = -1
  }

  private acquire(
    x: number, y: number,
    count: number, eActive: Uint8Array, ex: Float32Array, ey: Float32Array,
  ): number {
    let best = -1
    let bestD = HOMING.acquire * HOMING.acquire
    for (let e = 0; e < count; e += 1) {
      if (!eActive[e]) continue
      const dx = ex[e] - x
      const dy = ey[e] - y
      const d2 = dx * dx + dy * dy
      if (d2 < bestD) { bestD = d2; best = e }
    }
    return best
  }

  private sync(): void {
    let n = 0
    for (let i = 0; i < MAX; i += 1) {
      if (!this.active[i]) continue
      const a = Math.atan2(this.vy[i], this.vx[i]) - Math.PI / 2
      this.dummy.position.set(this.x[i], this.y[i], 0.3)
      this.dummy.rotation.set(0, 0, a)
      this.dummy.scale.setScalar(1)
      this.dummy.updateMatrix()
      this.mesh.setMatrixAt(n, this.dummy.matrix)

      // Plume trails behind the missile and shrinks with remaining life.
      const k = Math.max(0.3, this.life[i] / HOMING.lifetime)
      this.dummy.position.set(
        this.x[i] - Math.cos(a + Math.PI / 2) * 0.34,
        this.y[i] - Math.sin(a + Math.PI / 2) * 0.34,
        0.25,
      )
      this.dummy.scale.setScalar(k)
      this.dummy.updateMatrix()
      this.trail.setMatrixAt(n, this.dummy.matrix)
      n += 1
    }
    this.mesh.count = n
    this.trail.count = n
    this.mesh.instanceMatrix.needsUpdate = true
    this.trail.instanceMatrix.needsUpdate = true
  }

  reset(): void {
    this.active.fill(0)
    this.remaining = 0
    this.volleyTimer = 0
    this.sync()
  }

  dispose(): void {
    this.mesh.geometry.dispose()
    this.trail.geometry.dispose()
    ;(this.mesh.material as THREE.Material).dispose()
    ;(this.trail.material as THREE.Material).dispose()
  }
}
